import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { API, api } from "@/lib/api";
import { ROLE_LABEL } from "@/lib/roles";
import {
  ArrowLeft, Pencil, Check, X, IdentificationCard, Receipt, FileText,
  CurrencyEur, FloppyDisk, Plus, Trash, UploadSimple, Download, Buildings,
  Calendar, FilePdf, FileImage, Wallet, FileXls, CaretDown, Key,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const fmtEUR2 = (n) => {
  const v = Math.trunc((Number(n) || 0) * 100) / 100;
  return new Intl.NumberFormat("es-ES", {
    style: "currency", currency: "EUR",
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(v);
};
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("es-ES") : "—";
const TODAY = new Date().toISOString().slice(0, 10);

const TIPOS_DOC = [
  { v: "contrato", label: "Contrato" },
  { v: "dni", label: "DNI" },
  { v: "cif", label: "CIF" },
  { v: "titulacion", label: "Titulación" },
  { v: "seguro_rc", label: "Seguro RC" },
  { v: "otro", label: "Otro" },
];

const CATEGORIAS_GASTO = [
  "Combustible", "Dietas", "Kilometraje", "Material", "Peajes", "Otros",
];

export default function PeritoDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [aseguradoras, setAseguradoras] = useState([]);
  const [tab, setTab] = useState("ficha");
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);

  const load = async () => {
    const [u, a] = await Promise.all([
      api.get(`/peritos/${id}`),
      api.get("/aseguradoras"),
    ]);
    setP(u.data);
    setForm(u.data);
    setAseguradoras((a.data || []).filter((x) => x.active !== false));
  };
  useEffect(() => { load(); }, [id]);

  if (!p) return null;
  const isExterno = p.role === "perito_externo";

  const save = async () => {
    const payload = {
      name: form.name,
      email: form.email,
      role: form.role,
      active: form.active,
      dni: form.dni || null,
      cif: form.cif || null,
      direccion_fiscal: form.direccion_fiscal || null,
      telefono: form.telefono || null,
      iban: form.iban || null,
      irpf_pct: form.irpf_pct ?? null,
      comision_default_pct: form.comision_default_pct ?? null,
      comisiones_por_aseguradora: form.comisiones_por_aseguradora || [],
      titulacion: form.titulacion || null,
      apcas_numero: form.apcas_numero || null,
      notas: form.notas || null,
      tipo_contrato: form.tipo_contrato || "empleado",
      is_colector: !!form.is_colector,
    };
    await api.put(`/peritos/${id}`, payload);
    setEdit(false);
    load();
  };

  return (
    <div data-testid="perito-detail">
      <header className="h-14 border-b border-border bg-white px-6 flex items-center justify-between sticky top-0 z-20">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => navigate("/admin/peritos")} className="text-muted hover:text-foreground inline-flex items-center gap-1 text-xs">
            <ArrowLeft className="w-3.5 h-3.5" weight="bold" /> Usuarios
          </button>
          <div className="text-muted">·</div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-muted">{ROLE_LABEL[p.role] || p.role}</div>
            <h1 className="font-display font-bold text-lg tracking-tight truncate">{p.name}</h1>
          </div>
          {!p.active && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-status-error-bg text-status-error-fg border border-status-error-bd">Inactivo</span>
          )}
        </div>
      </header>

      <div className="border-b border-border bg-white px-6 flex gap-1 overflow-x-auto">
        {[
          { id: "ficha", label: "Ficha", icon: IdentificationCard },
          { id: "fiscal", label: "Datos fiscales", icon: Receipt },
          ...(isExterno ? [
            { id: "comisiones", label: "Comisiones", icon: CurrencyEur },
            { id: "liquidacion", label: "Liquidación", icon: Wallet },
          ] : [
            { id: "liquidacion-interna", label: "Liquidación interna", icon: Wallet },
          ]),
          { id: "gastos", label: "Gastos", icon: CurrencyEur },
          { id: "nominas", label: "Nóminas", icon: Receipt },
          { id: "documentos", label: "Documentación", icon: FileText },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`tab-${t.id}`}
            className={`px-3 py-2 text-xs font-semibold border-b-2 transition-colors inline-flex items-center gap-1.5 whitespace-nowrap ${
              tab === t.id ? "border-foreground text-foreground" : "border-transparent text-muted hover:text-foreground"
            }`}
          >
            <t.icon className="w-3.5 h-3.5" weight="bold" />{t.label}
          </button>
        ))}
      </div>

      {tab === "ficha" && (
        <div className="p-6 max-w-3xl space-y-4">
          {!edit ? (
            <>
              <FieldShow label="Código" value={p.code} mono />
              <FieldShow label="Nombre" value={p.name} />
              <FieldShow label="Email" value={p.email} />
              <FieldShow label="Rol" value={ROLE_LABEL[p.role] || p.role} />
              <FieldShow label="Estado" value={p.active ? "Activo" : "Inactivo"} />
              <FieldShow label="Alta" value={fmtDate(p.created_at)} />
              <div className="flex flex-wrap gap-2 pt-1">
                <button onClick={() => setEdit(true)} className="text-xs border border-border hover:border-foreground rounded-md px-3 py-1.5 inline-flex items-center gap-1.5">
                  <Pencil className="w-3 h-3" weight="bold" /> Editar
                </button>
                <button
                  onClick={() => setResetOpen(true)}
                  data-testid="reset-password-btn"
                  className="text-xs border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-900 rounded-md px-3 py-1.5 inline-flex items-center gap-1.5 font-semibold"
                >
                  <Key className="w-3 h-3" weight="bold" /> Reiniciar contraseña
                </button>
              </div>
            </>
          ) : (
            <div className="space-y-3">
              <Field label="Nombre"><Inp value={form.name} onChange={(v) => setForm({ ...form, name: v })} /></Field>
              <Field label="Email"><Inp type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} /></Field>
              <Field label="Rol">
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full border border-border rounded-md px-3 py-2 text-sm bg-white">
                  <option value="admin">{ROLE_LABEL.admin}</option>
                  <option value="gestor_senior">{ROLE_LABEL.gestor_senior}</option>
                  <option value="gestor_siniestros">{ROLE_LABEL.gestor_siniestros}</option>
                  <option value="perito_interno">{ROLE_LABEL.perito_interno}</option>
                  <option value="perito_externo">{ROLE_LABEL.perito_externo}</option>
                  <option value="tramitador_telematico">{ROLE_LABEL.tramitador_telematico}</option>
                </select>
              </Field>
              <Field label="Activo">
                <label className="text-sm inline-flex items-center gap-2"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="rounded border-border" />Activo</label>
              </Field>
              <Field label="Colector de documentación" hint="Si está marcado, este usuario recibe en su bandeja las solicitudes de documentación que crean los peritos internos. Puede cogerlas y entregar los archivos al expediente.">
                <label className="text-sm inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={!!form.is_colector}
                    onChange={(e) => setForm({ ...form, is_colector: e.target.checked })}
                    className="rounded border-border"
                    data-testid="field-is-colector"
                  />
                  Recibe solicitudes
                </label>
              </Field>
              <SaveCancel onSave={save} onCancel={() => { setForm(p); setEdit(false); }} />
            </div>
          )}
        </div>
      )}

      {tab === "fiscal" && (
        <div className="p-6 max-w-3xl space-y-4">
          <Section title="Identificación">
            <Grid>
              <Field label="DNI"><Inp value={form.dni || ""} onChange={(v) => setForm({ ...form, dni: v.toUpperCase() })} mono /></Field>
              <Field label="CIF (si autónomo/empresa)"><Inp value={form.cif || ""} onChange={(v) => setForm({ ...form, cif: v.toUpperCase() })} mono /></Field>
            </Grid>
            <Field label="Dirección fiscal"><Inp value={form.direccion_fiscal || ""} onChange={(v) => setForm({ ...form, direccion_fiscal: v })} /></Field>
            <Field label="Teléfono"><Inp value={form.telefono || ""} onChange={(v) => setForm({ ...form, telefono: v })} /></Field>
          </Section>
          <Section title="Bancarios">
            <Field label="IBAN"><Inp value={form.iban || ""} onChange={(v) => setForm({ ...form, iban: v.toUpperCase() })} mono /></Field>
            <Field label="Retención IRPF (%)" hint="0-15. Aplicable a profesionales autónomos.">
              <NumInp value={form.irpf_pct ?? ""} onChange={(v) => setForm({ ...form, irpf_pct: v })} step={0.5} min={0} max={50} suffix="%" />
            </Field>
            <Field label="Tipo de contrato" hint="Determina el cálculo del Coste Empresa en Rendimiento. Empleado: Liquidación × 1,6. Autónomo/Empresa: igual a Liquidación. Socio: se excluye por defecto del cuadro de rendimiento.">
              <select
                value={form.tipo_contrato || "empleado"}
                onChange={(e) => setForm({ ...form, tipo_contrato: e.target.value })}
                data-testid="field-tipo-contrato"
                className="block w-full border border-border rounded-md px-3 py-2 text-sm bg-white"
              >
                <option value="empleado">Empleado</option>
                <option value="autonomo">Autónomo</option>
                <option value="empresa">Empresa</option>
                <option value="socio">Socio del gabinete</option>
              </select>
            </Field>
          </Section>
          <ProfesionalSection
            peritoId={id}
            form={form}
            setForm={setForm}
            onFirmaChange={(p) => { setP(p); setForm(p); }}
          />
          <Field label="Notas internas">
            <textarea value={form.notas || ""} onChange={(e) => setForm({ ...form, notas: e.target.value })} rows={3} className="w-full border border-border rounded-md px-3 py-2 text-sm" />
          </Field>
          <SaveCancel onSave={save} onCancel={() => setForm(p)} disabled={JSON.stringify(form) === JSON.stringify(p)} />
        </div>
      )}

      {tab === "comisiones" && isExterno && (
        <ComisionesTab
          form={form}
          setForm={setForm}
          aseguradoras={aseguradoras}
          onSave={save}
          dirty={JSON.stringify(form) !== JSON.stringify(p)}
        />
      )}

      {tab === "liquidacion" && isExterno && <LiquidacionTab peritoId={id} />}
      {tab === "liquidacion-interna" && !isExterno && <LiquidacionInternaTab peritoId={id} />}

      {tab === "gastos" && <GastosTab peritoId={id} />}
      {tab === "nominas" && <NominasTab peritoId={id} />}
      {tab === "documentos" && <DocumentosTab peritoId={id} />}

      {resetOpen && (
        <ResetPasswordModal
          email={p.email}
          name={p.name}
          onClose={() => setResetOpen(false)}
        />
      )}
    </div>
  );
}

// ===== Reset password modal =====
function ResetPasswordModal({ email, name, onClose }) {
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres.");
      return;
    }
    if (pwd !== pwd2) {
      toast.error("Las contraseñas no coinciden.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/admin-reset-password", { email, new_password: pwd });
      toast.success(`Contraseña reiniciada para ${name}.`);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo reiniciar la contraseña.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="reset-password-modal"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-white rounded-xl ring-1 ring-zinc-200 shadow-xl w-full max-w-sm p-6 space-y-4"
      >
        <div>
          <h2 className="font-display font-black text-lg">Reiniciar contraseña</h2>
          <p className="text-xs text-muted mt-1">
            Para <strong>{name}</strong> · {email}
          </p>
        </div>
        <Field label="Nueva contraseña">
          <div className="relative">
            <input
              type={show ? "text" : "password"}
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              autoFocus
              data-testid="reset-pwd-1"
              className="w-full border border-border rounded-md px-3 py-2 text-sm pr-10"
              placeholder="Mínimo 6 caracteres"
            />
            <button
              type="button"
              onClick={() => setShow((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted hover:text-foreground"
            >
              {show ? "Ocultar" : "Ver"}
            </button>
          </div>
        </Field>
        <Field label="Repite la contraseña">
          <input
            type={show ? "text" : "password"}
            value={pwd2}
            onChange={(e) => setPwd2(e.target.value)}
            data-testid="reset-pwd-2"
            className="w-full border border-border rounded-md px-3 py-2 text-sm"
          />
        </Field>
        <div className="text-[11px] text-muted bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          Se aplicará inmediatamente. El usuario podrá entrar con la nueva contraseña sin cambio forzado.
        </div>
        <div className="flex gap-2 justify-end pt-1">
          <button
            type="button"
            onClick={onClose}
            data-testid="reset-cancel"
            className="text-xs border border-border hover:border-foreground rounded-md px-3 py-2"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={busy}
            data-testid="reset-submit"
            className="text-xs bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white rounded-md px-4 py-2 font-semibold inline-flex items-center gap-1.5"
          >
            <Key className="w-3 h-3" weight="bold" />
            {busy ? "Aplicando..." : "Reiniciar"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ===== Profesional (titulación + firma) =====
function ProfesionalSection({ peritoId, form, setForm, onFirmaChange }) {
  const [busy, setBusy] = useState(false);
  const fileInput = useRef(null);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post(`/peritos/${peritoId}/firma`, fd);
      onFirmaChange(r.data);
    } catch (err) {
      alert(err?.response?.data?.detail || "Error subiendo firma");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const remove = async () => {
    if (!confirm("¿Eliminar la firma actual?")) return;
    setBusy(true);
    try {
      const r = await api.delete(`/peritos/${peritoId}/firma`);
      onFirmaChange(r.data);
    } finally {
      setBusy(false);
    }
  };

  // La auth de las URLs directas (img src / a href) viaja por la cookie
  // httpOnly `ekspertium_auth` — no necesitamos `?token=` en JS.
  const firmaUrl = form.firma_path
    ? `${API}/peritos/${peritoId}/archivos/${form.firma_path}?v=${Date.now()}`
    : null;

  return (
    <Section
      title="Profesional"
      desc="Aparecerá automáticamente en el pie de los informes finales firmados por este perito."
    >
      <Field
        label="Titulación / colegiación"
        hint='Ej.: "Arquitecto Técnico · Col. Nº 1234 COAATIE Bizkaia"'
      >
        <textarea
          value={form.titulacion || ""}
          onChange={(e) => setForm({ ...form, titulacion: e.target.value })}
          rows={2}
          data-testid="set-titulacion"
          className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-foreground/20 focus:border-foreground"
        />
      </Field>
      <Field label="Nº Perito I.R.D. APCAS" hint="Se muestra en el bloque legal del informe final.">
        <Inp
          value={form.apcas_numero || ""}
          onChange={(v) => setForm({ ...form, apcas_numero: v })}
          mono
        />
      </Field>
      <Field
        label="Firma digitalizada"
        hint="Imagen PNG con fondo transparente recomendada (también JPG/WEBP)."
      >
        <div className="flex items-center gap-4">
          <div
            className="w-44 h-20 bg-slate-50 border border-dashed border-border rounded-md flex items-center justify-center overflow-hidden flex-shrink-0"
            data-testid="firma-preview"
          >
            {firmaUrl ? (
              <img src={firmaUrl} alt="firma" className="max-w-full max-h-full object-contain" />
            ) : (
              <span className="text-xs text-muted">Sin firma</span>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              disabled={busy}
              data-testid="firma-upload-btn"
              className="text-xs border border-border hover:border-foreground rounded-md px-3 py-1.5 inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              <UploadSimple className="w-3 h-3" weight="bold" />
              {form.firma_path ? "Reemplazar firma" : "Subir firma"}
            </button>
            {form.firma_path && (
              <button
                type="button"
                onClick={remove}
                disabled={busy}
                data-testid="firma-remove-btn"
                className="text-xs text-status-error-fg hover:bg-status-error-bg rounded-md px-3 py-1.5 inline-flex items-center gap-1.5 disabled:opacity-50"
              >
                <Trash className="w-3 h-3" weight="bold" /> Eliminar
              </button>
            )}
            <input
              ref={fileInput}
              type="file"
              accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp"
              onChange={upload}
              className="hidden"
            />
          </div>
        </div>
      </Field>
    </Section>
  );
}

// ===== Comisiones =====
function ComisionesTab({ form, setForm, aseguradoras, onSave, dirty }) {
  const map = useMemo(() => {
    const m = new Map();
    for (const c of (form.comisiones_por_aseguradora || [])) m.set(c.aseguradora_id, c.pct);
    return m;
  }, [form]);

  const setPct = (asegId, pct) => {
    const arr = (form.comisiones_por_aseguradora || []).filter((c) => c.aseguradora_id !== asegId);
    if (pct !== "" && pct !== null) arr.push({ aseguradora_id: asegId, pct: Number(pct) });
    setForm({ ...form, comisiones_por_aseguradora: arr });
  };

  return (
    <div className="p-6 max-w-3xl space-y-4">
      <Section title="Comisión por defecto" desc="Se aplica a aseguradoras sin % personalizado.">
        <Field label="% por defecto">
          <NumInp value={form.comision_default_pct ?? ""} onChange={(v) => setForm({ ...form, comision_default_pct: v })} step={0.5} min={0} max={100} suffix="%" placeholder="ej. 50" />
        </Field>
      </Section>
      <Section title={`Comisiones personalizadas por aseguradora (${map.size}/${aseguradoras.length})`} desc="Deja en blanco para usar el % por defecto.">
        <div className="border border-border rounded-md bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="thead-modern">
              <tr>
                <th className="text-left px-4 py-2">Aseguradora</th>
                <th className="text-right px-4 py-2 w-32">% Comisión</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {aseguradoras.map((a) => (
                <tr key={a.id}>
                  <td className="px-4 py-2"><span className="inline-flex items-center gap-2"><Buildings className="w-3 h-3 text-muted" weight="bold" />{a.name}</span></td>
                  <td className="px-4 py-2">
                    <NumInp
                      value={map.get(a.id) ?? ""}
                      onChange={(v) => setPct(a.id, v)}
                      step={0.5} min={0} max={100} suffix="%"
                      placeholder={form.comision_default_pct != null ? `${form.comision_default_pct}` : ""}
                      data-testid={`pct-${a.code}`}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
      {dirty && <SaveCancel onSave={onSave} onCancel={() => window.location.reload()} />}
    </div>
  );
}

// ===== Liquidación =====
function LiquidacionTab({ peritoId }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState("pendientes"); // pendientes | liquidadas | todas
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (desde) params.set("desde", desde);
    if (hasta) params.set("hasta", hasta);
    const qs = params.toString();
    try {
      const r = await api.get(`/peritos/${peritoId}/liquidacion${qs ? `?${qs}` : ""}`);
      setData(r.data);
    } catch (e) {
      setData(null);
      console.error(e);
    } finally {
      setLoading(false);
      setSelected(new Set());
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [peritoId]);

  const items = useMemo(() => {
    if (!data) return [];
    if (filtro === "pendientes") return data.items.filter((i) => !i.liquidada);
    if (filtro === "liquidadas") return data.items.filter((i) => i.liquidada);
    return data.items;
  }, [data, filtro]);

  const toggle = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };
  const toggleAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map((i) => i.expediente_id)));
  };

  const marcar = async (liquidar) => {
    if (selected.size === 0) return;
    if (!confirm(`¿${liquidar ? "Marcar como liquidados" : "Reabrir como pendientes"} ${selected.size} expediente(s)?`)) return;
    setBusy(true);
    try {
      await api.post(`/peritos/${peritoId}/liquidacion/marcar`, {
        expediente_ids: Array.from(selected),
        liquidar,
      });
      await load();
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="p-6 text-sm text-muted">Cargando liquidación…</div>;
  if (!data) return <div className="p-6 text-sm text-status-error-fg">Error al cargar.</div>;

  const t = data.totales;
  const sumSeleccion = items
    .filter((i) => selected.has(i.expediente_id))
    .reduce((s, i) => s + i.comision, 0);

  return (
    <div className="p-6 max-w-6xl space-y-4">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Expedientes" value={t.n_expedientes} />
        <Kpi label="Minuta total" value={fmtEUR2(t.minuta_total)} />
        <Kpi label="Comisión pendiente" value={fmtEUR2(t.comision_pendiente)} accent="amber" />
        <Kpi label="Comisión liquidada" value={fmtEUR2(t.comision_liquidada)} accent="emerald" />
      </div>

      {/* Filtros */}
      <div className="bg-white border border-border rounded-md p-4 flex flex-wrap items-end gap-3">
        <Field label="Desde">
          <Inp type="date" value={desde} onChange={setDesde} />
        </Field>
        <Field label="Hasta">
          <Inp type="date" value={hasta} onChange={setHasta} />
        </Field>
        <div className="inline-flex rounded-md border border-border overflow-hidden">
          {[
            { v: "pendientes", l: "Pendientes" },
            { v: "liquidadas", l: "Liquidadas" },
            { v: "todas", l: "Todas" },
          ].map((f) => (
            <button
              key={f.v}
              onClick={() => setFiltro(f.v)}
              data-testid={`filter-${f.v}`}
              className={`text-xs px-3 py-2 ${filtro === f.v ? "bg-foreground text-white" : "bg-white hover:bg-slate-50"}`}
            >
              {f.l}
            </button>
          ))}
        </div>
        <button
          onClick={load}
          data-testid="reload-liquidacion"
          className="text-xs border border-border hover:border-foreground rounded-md px-3 py-2 ml-auto"
        >
          Actualizar
        </button>
        <ExportButtons peritoId={peritoId} desde={desde} hasta={hasta} disabled={t.n_expedientes === 0} />
      </div>

      {/* Resumen por aseguradora */}
      {data.grupos.length > 0 && (
        <Section title="Resumen por aseguradora" desc="Suma de comisiones por compañía sobre el total de expedientes con minuta.">
          <div className="border border-border rounded-md bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead className="thead-modern">
                <tr>
                  <th className="text-left px-4 py-2">Aseguradora</th>
                  <th className="text-right px-4 py-2 w-20">%</th>
                  <th className="text-right px-4 py-2 w-16">Nº</th>
                  <th className="text-right px-4 py-2">Minuta</th>
                  <th className="text-right px-4 py-2">Comisión</th>
                  <th className="text-right px-4 py-2">Pendiente</th>
                  <th className="text-right px-4 py-2">Liquidada</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.grupos.map((g) => (
                  <tr key={g.aseguradora_id || "no-aseg"}>
                    <td className="px-4 py-2">
                      <span className="inline-flex items-center gap-2">
                        <Buildings className="w-3 h-3 text-muted" weight="bold" />
                        {g.aseguradora_name}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-xs">
                      {g.pct_aplicado != null ? `${g.pct_aplicado}%` : "—"}
                      {g.pct_personalizado && (
                        <span className="ml-1 text-[9px] text-violet-600 font-semibold uppercase">★</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-xs">{g.n_expedientes}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-xs">{fmtEUR2(g.minuta_total)}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-xs font-semibold">{fmtEUR2(g.comision_total)}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-xs text-amber-700">{fmtEUR2(g.comision_pendiente)}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-xs text-emerald-700">{fmtEUR2(g.comision_liquidada)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {/* Acciones masivas */}
      {selected.size > 0 && (
        <div className="sticky top-14 z-10 bg-foreground text-white rounded-md px-4 py-2 flex items-center justify-between shadow-md">
          <div className="text-xs">
            <span className="font-semibold">{selected.size} seleccionados</span>
            <span className="mx-2 opacity-50">·</span>
            Comisión total: <span className="font-semibold tabular-nums">{fmtEUR2(sumSeleccion)}</span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => marcar(true)}
              disabled={busy}
              data-testid="mark-paid"
              className="text-xs bg-emerald-500 hover:bg-emerald-600 px-3 py-1.5 rounded-md font-medium inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              <Check className="w-3 h-3" weight="bold" /> Marcar liquidada
            </button>
            <button
              onClick={() => marcar(false)}
              disabled={busy}
              data-testid="mark-unpaid"
              className="text-xs bg-amber-500 hover:bg-amber-600 px-3 py-1.5 rounded-md font-medium inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              <X className="w-3 h-3" weight="bold" /> Reabrir
            </button>
            <button onClick={() => setSelected(new Set())} className="text-xs px-3 py-1.5 opacity-70 hover:opacity-100">
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Tabla detalle */}
      <Section title={`Expedientes (${items.length})`} desc={`Filtro: ${filtro}. La comisión se calcula sobre la minuta cobrada con el % aplicable.`}>
        <div className="border border-border rounded-md bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="thead-modern">
              <tr>
                <th className="px-4 py-2 w-8">
                  <input
                    type="checkbox"
                    checked={items.length > 0 && selected.size === items.length}
                    onChange={toggleAll}
                    data-testid="select-all"
                    className="rounded border-border"
                  />
                </th>
                <th className="text-left px-3 py-2">Cierre</th>
                <th className="text-left px-3 py-2">Nº siniestro / asegurado</th>
                <th className="text-left px-3 py-2">Aseguradora</th>
                <th className="text-right px-3 py-2 w-20">%</th>
                <th className="text-right px-3 py-2">Minuta</th>
                <th className="text-right px-3 py-2">Comisión</th>
                <th className="text-center px-3 py-2 w-24">Estado</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center py-12 text-muted text-xs">
                    Sin expedientes en este filtro.
                  </td>
                </tr>
              ) : items.map((i) => (
                <tr key={i.expediente_id} className={selected.has(i.expediente_id) ? "bg-violet-50/40" : ""}>
                  <td className="px-4 py-2">
                    <input
                      type="checkbox"
                      checked={selected.has(i.expediente_id)}
                      onChange={() => toggle(i.expediente_id)}
                      data-testid={`row-${i.expediente_id}`}
                      className="rounded border-border"
                    />
                  </td>
                  <td className="px-3 py-2 text-xs tabular-nums">{fmtDate(i.fecha_cierre)}</td>
                  <td className="px-3 py-2 text-xs">
                    <div className="font-mono text-[11px]">{i.numero_siniestro || "—"}</div>
                    <div className="text-muted text-[10px] truncate max-w-[280px]">{i.asegurado_nombre || ""}</div>
                  </td>
                  <td className="px-3 py-2 text-xs">{i.aseguradora || "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-xs">
                    {i.pct_aplicado != null ? `${i.pct_aplicado}%` : "—"}
                    {i.pct_personalizado && <span className="ml-1 text-[9px] text-violet-600">★</span>}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-xs">{fmtEUR2(i.importe_minuta)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-xs font-semibold">{fmtEUR2(i.comision)}</td>
                  <td className="px-3 py-2 text-center">
                    {i.liquidada ? (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                        Liquidada
                      </span>
                    ) : (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                        Pendiente
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => navigate(`/expediente/${i.expediente_id}`)}
                      className="text-violet-600 hover:underline text-[10px]"
                      title="Abrir expediente"
                    >
                      Abrir →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
}

function Kpi({ label, value, accent }) {
  const tone = accent === "amber"
    ? "border-amber-200 bg-amber-50/40"
    : accent === "emerald"
    ? "border-emerald-200 bg-emerald-50/40"
    : "border-border bg-white";
  return (
    <div className={`rounded-md border ${tone} p-4`}>
      <div className="text-[10px] uppercase tracking-widest text-muted font-semibold">{label}</div>
      <div className="font-display text-xl font-bold tabular-nums mt-1">{value}</div>
    </div>
  );
}

function ExportButtons({ peritoId, desde, hasta, disabled }) {
  const [open, setOpen] = useState(false);
  const buildUrl = (kind) => {
    // Auth viaja por cookie httpOnly `ekspertium_auth`; no añadimos token.
    const params = new URLSearchParams();
    if (desde) params.set("desde", desde);
    if (hasta) params.set("hasta", hasta);
    const qs = params.toString();
    return `${API}/peritos/${peritoId}/liquidacion/export.${kind}${qs ? `?${qs}` : ""}`;
  };
  return (
    <div className="relative">
      <button
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        data-testid="export-liquidacion"
        className="text-xs bg-foreground text-white hover:bg-foreground/90 rounded-md px-3 py-2 inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <Download className="w-3 h-3" weight="bold" /> Exportar
        <CaretDown className="w-3 h-3 opacity-70" weight="bold" />
      </button>
      {open && !disabled && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-20 bg-white border border-border rounded-md shadow-lg overflow-hidden min-w-[200px]">
            <a
              href={buildUrl("pdf")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="export-pdf"
              className="flex items-center gap-2 px-3 py-2 text-xs hover:bg-slate-50"
              onClick={() => setOpen(false)}
            >
              <FilePdf className="w-4 h-4 text-red-600" weight="duotone" />
              <div>
                <div className="font-semibold">PDF</div>
                <div className="text-[10px] text-muted">Liquidación firmable</div>
              </div>
            </a>
            <a
              href={buildUrl("xlsx")}
              data-testid="export-xlsx"
              className="flex items-center gap-2 px-3 py-2 text-xs hover:bg-slate-50 border-t border-border"
              onClick={() => setOpen(false)}
            >
              <FileXls className="w-4 h-4 text-emerald-600" weight="duotone" />
              <div>
                <div className="font-semibold">Excel</div>
                <div className="text-[10px] text-muted">Hoja de cálculo editable</div>
              </div>
            </a>
          </div>
        </>
      )}
    </div>
  );
}

// ===== Liquidación interna (perito interno · admin only) =====
function LiquidacionInternaTab({ peritoId }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [filtro, setFiltro] = useState("todas"); // todas | pendientes | facturadas | cobradas

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (desde) params.set("desde", desde);
      if (hasta) params.set("hasta", hasta);
      const qs = params.toString();
      const r = await api.get(`/peritos/${peritoId}/liquidacion-interna${qs ? `?${qs}` : ""}`);
      setData(r.data);
    } catch (e) {
      console.error(e); setData(null);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [peritoId]);

  const items = useMemo(() => {
    if (!data) return [];
    if (filtro === "pendientes") return data.items.filter((i) => !i.facturado_at);
    if (filtro === "facturadas") return data.items.filter((i) => i.facturado_at && !i.cobrado_at);
    if (filtro === "cobradas") return data.items.filter((i) => i.cobrado_at);
    return data.items;
  }, [data, filtro]);

  if (loading) return <div className="p-6 text-sm text-muted">Cargando…</div>;
  if (!data) return <div className="p-6 text-sm text-status-error-fg">Error al cargar.</div>;

  const t = data.totales;

  return (
    <div className="p-6 max-w-6xl space-y-4" data-testid="liquidacion-interna-tab">
      <div className="bg-amber-50 border border-amber-200 text-amber-900 text-xs rounded-md px-3 py-2">
        <strong>Vista del administrador.</strong> Resumen de la actividad facturable del perito al despacho.
        El perito interno no recibe comisión (cobra nómina). Sirve para contrastar con cada compañía a final de mes
        y, próximamente, evaluar el rendimiento individual.
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi label="Expedientes" value={t.n_expedientes} />
        <Kpi label="Tasación total" value={fmtEUR2(t.tasacion_total)} />
        <Kpi label="Minuta total" value={fmtEUR2(t.minuta_total)} />
        <Kpi label="Pte. facturar" value={fmtEUR2(t.pendiente_facturar)} accent="amber" />
        <Kpi label="Cobrado" value={fmtEUR2(t.cobrado_total)} accent="emerald" />
      </div>

      {/* Filtros */}
      <div className="bg-white border border-border rounded-md p-4 flex flex-wrap items-end gap-3">
        <Field label="Desde"><Inp type="date" value={desde} onChange={setDesde} /></Field>
        <Field label="Hasta"><Inp type="date" value={hasta} onChange={setHasta} /></Field>
        <div className="inline-flex rounded-md border border-border overflow-hidden">
          {[
            { v: "todas", l: "Todas" },
            { v: "pendientes", l: "Pendientes" },
            { v: "facturadas", l: "Facturadas" },
            { v: "cobradas", l: "Cobradas" },
          ].map((f) => (
            <button
              key={f.v}
              onClick={() => setFiltro(f.v)}
              data-testid={`liq-int-filter-${f.v}`}
              className={`text-xs px-3 py-2 ${filtro === f.v ? "bg-foreground text-white" : "bg-white hover:bg-slate-50"}`}
            >{f.l}</button>
          ))}
        </div>
        <button onClick={load} className="text-xs border border-border hover:border-foreground rounded-md px-3 py-2 ml-auto" data-testid="liq-int-reload">
          Actualizar
        </button>
      </div>

      {/* Resumen por aseguradora */}
      {data.grupos.length > 0 && (
        <Section title="Resumen por aseguradora">
          <div className="border border-border rounded-md bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead className="thead-modern">
                <tr>
                  <th className="text-left px-4 py-2">Aseguradora</th>
                  <th className="text-right px-4 py-2 w-16">Nº</th>
                  <th className="text-right px-4 py-2">Tasación</th>
                  <th className="text-right px-4 py-2">Minuta</th>
                  <th className="text-right px-4 py-2">Facturada</th>
                  <th className="text-right px-4 py-2">Cobrada</th>
                </tr>
              </thead>
              <tbody>
                {data.grupos.map((g) => (
                  <tr key={g.aseguradora_id || g.aseguradora_name} className="border-t border-border">
                    <td className="px-4 py-2 font-medium">{g.aseguradora_name}</td>
                    <td className="px-4 py-2 text-right">{g.n_expedientes}</td>
                    <td className="px-4 py-2 text-right">{fmtEUR2(g.tasacion_total)}</td>
                    <td className="px-4 py-2 text-right font-semibold">{fmtEUR2(g.minuta_total)}</td>
                    <td className="px-4 py-2 text-right text-amber-700">{fmtEUR2(g.facturado_total)}</td>
                    <td className="px-4 py-2 text-right text-emerald-700">{fmtEUR2(g.cobrado_total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {/* Detalle */}
      <Section title="Detalle" desc={`${items.length} expediente(s)`}>
        <div className="border border-border rounded-md bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="thead-modern">
                <tr>
                  <th className="text-left px-3 py-2">Cierre</th>
                  <th className="text-left px-3 py-2">Aseguradora</th>
                  <th className="text-left px-3 py-2">Nº Siniestro</th>
                  <th className="text-left px-3 py-2">Asegurado</th>
                  <th className="text-right px-3 py-2">Tasación</th>
                  <th className="text-right px-3 py-2">Minuta</th>
                  <th className="text-left px-3 py-2 w-28">Estado</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr><td colSpan={8} className="px-4 py-6 text-center text-muted">Sin resultados.</td></tr>
                ) : items.map((it) => {
                  const estado = it.cobrado_at ? "Cobrado" : (it.facturado_at ? "Facturado" : "Pendiente");
                  const cls = it.cobrado_at ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : (it.facturado_at ? "bg-amber-50 text-amber-700 border-amber-200"
                    : "bg-slate-50 text-slate-700 border-slate-200");
                  return (
                    <tr key={it.expediente_id} className="border-t border-border hover:bg-slate-50/40">
                      <td className="px-3 py-2 whitespace-nowrap">{fmtDate(it.fecha_cierre)}</td>
                      <td className="px-3 py-2">{it.aseguradora || "—"}</td>
                      <td className="px-3 py-2 font-mono text-xs">{it.numero_siniestro || "—"}</td>
                      <td className="px-3 py-2 truncate max-w-[200px]">{it.asegurado_nombre || "—"}</td>
                      <td className="px-3 py-2 text-right">{fmtEUR2(it.importe_tasacion)}</td>
                      <td className="px-3 py-2 text-right font-semibold">
                        {fmtEUR2(it.importe_minuta)}
                        {it.importe_minuta_manual && (
                          <span title="Minuta editada manualmente" className="ml-1 text-[9px] text-amber-700 font-bold">M</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full border ${cls}`}>{estado}</span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          onClick={() => navigate(`/review/${it.expediente_id}`)}
                          data-testid={`liq-int-open-${it.expediente_id}`}
                          className="text-[11px] underline text-muted hover:text-foreground"
                        >
                          Abrir
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </Section>
    </div>
  );
}



// ===== Gastos =====
function GastosTab({ peritoId }) {
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ fecha: TODAY, concepto: "", importe: "", categoria: "Combustible", notas: "" });

  const load = async () => {
    const r = await api.get(`/peritos/${peritoId}/gastos`);
    setItems(r.data);
  };
  useEffect(() => { load(); }, [peritoId]);

  const create = async () => {
    if (!form.concepto || !form.importe) return;
    await api.post(`/peritos/${peritoId}/gastos`, { ...form, importe: Number(form.importe) });
    setAdding(false);
    setForm({ fecha: TODAY, concepto: "", importe: "", categoria: "Combustible", notas: "" });
    load();
  };

  const del = async (g) => {
    if (!confirm(`¿Eliminar el gasto "${g.concepto}"?`)) return;
    await api.delete(`/peritos/${peritoId}/gastos/${g.id}`);
    load();
  };

  const total = items.reduce((s, g) => s + (g.importe || 0), 0);

  return (
    <div className="p-6 max-w-4xl space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-muted font-semibold">Total</div>
          <div className="font-display text-2xl font-bold tabular-nums">{fmtEUR2(total)}</div>
        </div>
        <button onClick={() => setAdding(true)} data-testid="add-gasto" className="bg-foreground text-white text-xs px-3 py-1.5 rounded-md font-medium inline-flex items-center gap-1.5">
          <Plus className="w-3 h-3" weight="bold" /> Nuevo gasto
        </button>
      </div>
      {adding && (
        <div className="bg-emerald-50/40 border border-emerald-200 rounded-md p-4 space-y-3">
          <Grid cols={4}>
            <Field label="Fecha"><Inp type="date" value={form.fecha} onChange={(v) => setForm({ ...form, fecha: v })} /></Field>
            <Field label="Categoría">
              <select value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })} className="w-full border border-border rounded-md px-3 py-2 text-sm bg-white">
                {CATEGORIAS_GASTO.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Concepto"><Inp value={form.concepto} onChange={(v) => setForm({ ...form, concepto: v })} placeholder="Repostaje" /></Field>
            <Field label="Importe (€)"><NumInp value={form.importe} onChange={(v) => setForm({ ...form, importe: v })} step={0.01} min={0} suffix="€" /></Field>
          </Grid>
          <div className="flex justify-end gap-2">
            <button onClick={() => setAdding(false)} className="text-xs px-3 py-1.5 text-muted">Cancelar</button>
            <button onClick={create} data-testid="save-gasto" className="bg-foreground text-white text-xs px-3 py-1.5 rounded-md">Crear gasto</button>
          </div>
        </div>
      )}
      <div className="border border-border rounded-md bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="thead-modern">
            <tr>
              <th className="text-left px-4 py-2">Fecha</th>
              <th className="text-left px-4 py-2">Categoría</th>
              <th className="text-left px-4 py-2">Concepto</th>
              <th className="text-right px-4 py-2">Importe</th>
              <th></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.length === 0 ? (
              <tr><td colSpan={5} className="text-center py-8 text-muted text-xs">Sin gastos registrados.</td></tr>
            ) : items.map((g) => (
              <tr key={g.id}>
                <td className="px-4 py-2 text-xs">{fmtDate(g.fecha)}</td>
                <td className="px-4 py-2 text-xs"><span className="text-[10px] bg-slate-100 px-1.5 py-0.5 rounded font-mono">{g.categoria || "—"}</span></td>
                <td className="px-4 py-2 text-xs">{g.concepto}</td>
                <td className="px-4 py-2 text-right tabular-nums text-xs font-semibold">{fmtEUR2(g.importe)}</td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => del(g)} className="p-1 text-muted hover:text-status-error-fg"><Trash className="w-3 h-3" weight="bold" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ===== Nóminas =====
function NominasTab({ peritoId }) {
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(false);
  const periodoActual = new Date().toISOString().slice(0, 7);
  const [form, setForm] = useState({ periodo: periodoActual, bruto: "", neto: "" });

  const load = async () => setItems((await api.get(`/peritos/${peritoId}/nominas`)).data);
  useEffect(() => { load(); }, [peritoId]);

  const create = async () => {
    if (!form.periodo || !form.bruto) return;
    const r = await api.post(`/peritos/${peritoId}/nominas`, {
      periodo: form.periodo, bruto: Number(form.bruto),
      neto: form.neto ? Number(form.neto) : null,
    });
    if (form.file) {
      const fd = new FormData();
      fd.append("file", form.file);
      await api.post(`/peritos/${peritoId}/nominas/${r.data.id}/archivo`, fd);
    }
    setAdding(false);
    setForm({ periodo: periodoActual, bruto: "", neto: "" });
    load();
  };

  const del = async (n) => {
    if (!confirm(`¿Eliminar la nómina de ${n.periodo}?`)) return;
    await api.delete(`/peritos/${peritoId}/nominas/${n.id}`);
    load();
  };

  const totalAnual = items.filter((n) => (n.periodo || "").startsWith(new Date().getFullYear().toString())).reduce((s, n) => s + (n.bruto || 0), 0);

  return (
    <div className="p-6 max-w-4xl space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-muted font-semibold">Total bruto {new Date().getFullYear()}</div>
          <div className="font-display text-2xl font-bold tabular-nums">{fmtEUR2(totalAnual)}</div>
        </div>
        <button onClick={() => setAdding(true)} data-testid="add-nomina" className="bg-foreground text-white text-xs px-3 py-1.5 rounded-md font-medium inline-flex items-center gap-1.5">
          <Plus className="w-3 h-3" weight="bold" /> Nueva nómina
        </button>
      </div>
      {adding && (
        <div className="bg-emerald-50/40 border border-emerald-200 rounded-md p-4 space-y-3">
          <Grid cols={3}>
            <Field label="Período (AAAA-MM)"><Inp value={form.periodo} onChange={(v) => setForm({ ...form, periodo: v })} placeholder="2026-05" /></Field>
            <Field label="Bruto"><NumInp value={form.bruto} onChange={(v) => setForm({ ...form, bruto: v })} step={0.01} min={0} suffix="€" /></Field>
            <Field label="Neto (opcional)"><NumInp value={form.neto} onChange={(v) => setForm({ ...form, neto: v })} step={0.01} min={0} suffix="€" /></Field>
          </Grid>
          <Field label="Archivo PDF (opcional)">
            <input type="file" accept=".pdf,application/pdf" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] })} className="text-xs" />
          </Field>
          <div className="flex justify-end gap-2">
            <button onClick={() => setAdding(false)} className="text-xs px-3 py-1.5 text-muted">Cancelar</button>
            <button onClick={create} data-testid="save-nomina" className="bg-foreground text-white text-xs px-3 py-1.5 rounded-md">Crear nómina</button>
          </div>
        </div>
      )}
      <div className="border border-border rounded-md bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="thead-modern">
            <tr>
              <th className="text-left px-4 py-2">Período</th>
              <th className="text-right px-4 py-2">Bruto</th>
              <th className="text-right px-4 py-2">Neto</th>
              <th className="text-left px-4 py-2">Archivo</th>
              <th></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.length === 0 ? (
              <tr><td colSpan={5} className="text-center py-8 text-muted text-xs">Sin nóminas registradas.</td></tr>
            ) : items.map((n) => (
              <tr key={n.id}>
                <td className="px-4 py-2 text-xs font-mono">{n.periodo}</td>
                <td className="px-4 py-2 text-right tabular-nums text-xs font-semibold">{fmtEUR2(n.bruto)}</td>
                <td className="px-4 py-2 text-right tabular-nums text-xs">{n.neto != null ? fmtEUR2(n.neto) : "—"}</td>
                <td className="px-4 py-2 text-xs">
                  {n.archivo_path ? (
                    <a
                      href={`${API}/peritos/${peritoId}/archivos/${n.archivo_path}`}
                      target="_blank" rel="noopener noreferrer"
                      className="text-violet-600 hover:underline inline-flex items-center gap-1"
                    >
                      <Download className="w-3 h-3" weight="bold" /> PDF
                    </a>
                  ) : <span className="text-muted">—</span>}
                </td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => del(n)} className="p-1 text-muted hover:text-status-error-fg"><Trash className="w-3 h-3" weight="bold" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ===== Documentos =====
function DocumentosTab({ peritoId }) {
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ tipo: "contrato", nombre: "", file: null });

  const load = async () => setItems((await api.get(`/peritos/${peritoId}/documentos`)).data);
  useEffect(() => { load(); }, [peritoId]);

  const upload = async () => {
    if (!form.file || !form.nombre) return;
    const fd = new FormData();
    fd.append("file", form.file);
    await api.post(
      `/peritos/${peritoId}/documentos?tipo=${encodeURIComponent(form.tipo)}&nombre=${encodeURIComponent(form.nombre)}`,
      fd,
    );
    setAdding(false);
    setForm({ tipo: "contrato", nombre: "", file: null });
    load();
  };

  const del = async (d) => {
    if (!confirm(`¿Eliminar el documento "${d.nombre}"?`)) return;
    await api.delete(`/peritos/${peritoId}/documentos/${d.id}`);
    load();
  };

  return (
    <div className="p-6 max-w-4xl space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-display font-bold text-sm tracking-tight">Documentación ({items.length})</h2>
        <button onClick={() => setAdding(true)} data-testid="add-doc" className="bg-foreground text-white text-xs px-3 py-1.5 rounded-md font-medium inline-flex items-center gap-1.5">
          <UploadSimple className="w-3 h-3" weight="bold" /> Subir documento
        </button>
      </div>
      {adding && (
        <div className="bg-emerald-50/40 border border-emerald-200 rounded-md p-4 space-y-3">
          <Grid cols={2}>
            <Field label="Tipo">
              <select value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })} className="w-full border border-border rounded-md px-3 py-2 text-sm bg-white">
                {TIPOS_DOC.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
              </select>
            </Field>
            <Field label="Nombre"><Inp value={form.nombre} onChange={(v) => setForm({ ...form, nombre: v })} placeholder="Ej: Contrato indefinido" /></Field>
          </Grid>
          <Field label="Archivo">
            <input type="file" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] })} className="text-xs" />
          </Field>
          <div className="flex justify-end gap-2">
            <button onClick={() => setAdding(false)} className="text-xs px-3 py-1.5 text-muted">Cancelar</button>
            <button onClick={upload} data-testid="save-doc" className="bg-foreground text-white text-xs px-3 py-1.5 rounded-md">Subir</button>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.length === 0 ? (
          <div className="col-span-2 text-center py-12 text-muted text-sm border border-dashed border-border rounded-md">Sin documentos.</div>
        ) : items.map((d) => {
          const isImg = /\.(jpg|jpeg|png|webp|gif|svg)$/i.test(d.archivo_path || "");
          const Icon = isImg ? FileImage : FilePdf;
          return (
            <div key={d.id} className="border border-border bg-white rounded-md p-4 group">
              <div className="flex items-start gap-3">
                <Icon className="w-8 h-8 text-violet-600 flex-shrink-0" weight="duotone" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{d.nombre}</div>
                  <div className="text-[10px] uppercase tracking-widest text-muted">{TIPOS_DOC.find((t) => t.v === d.tipo)?.label || d.tipo}</div>
                  <div className="text-[11px] text-muted inline-flex items-center gap-1 mt-1"><Calendar className="w-3 h-3" weight="bold" />{fmtDate(d.created_at)}</div>
                </div>
                <button onClick={() => del(d)} className="p-1 text-muted opacity-0 group-hover:opacity-100 hover:text-status-error-fg"><Trash className="w-3 h-3" weight="bold" /></button>
              </div>
              <a
                href={`${API}/peritos/${peritoId}/archivos/${d.archivo_path}`}
                target="_blank" rel="noopener noreferrer"
                className="text-xs text-violet-600 hover:underline inline-flex items-center gap-1 mt-3"
              >
                <Download className="w-3 h-3" weight="bold" /> Descargar
              </a>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ===== UI helpers =====
function Section({ title, desc, children }) {
  return (
    <section className="bg-white border border-border rounded-md p-5 space-y-3">
      <div>
        <h2 className="font-display font-bold text-sm tracking-tight">{title}</h2>
        {desc && <p className="text-[11px] text-muted mt-0.5">{desc}</p>}
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}
function Grid({ cols = 2, children }) {
  return <div className={`grid grid-cols-1 md:grid-cols-${cols} gap-3`}>{children}</div>;
}
function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-widest font-semibold text-muted mb-1">{label}</label>
      {children}
      {hint && <p className="text-[10px] text-muted mt-1">{hint}</p>}
    </div>
  );
}
function FieldShow({ label, value, mono }) {
  return (
    <div className="flex items-baseline gap-3">
      <div className="text-[11px] uppercase tracking-widest font-semibold text-muted w-32 flex-shrink-0">{label}</div>
      <div className={`text-sm flex-1 ${mono ? "font-mono" : ""} ${!value ? "text-muted italic" : ""}`}>{value || "—"}</div>
    </div>
  );
}
function Inp({ value, onChange, type = "text", mono, ...rest }) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-foreground/20 focus:border-foreground ${mono ? "font-mono" : ""}`}
      {...rest}
    />
  );
}
function NumInp({ value, onChange, suffix, ...rest }) {
  return (
    <div className="relative">
      <input
        type="number"
        value={value === null || value === undefined ? "" : value}
        onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
        className="w-full border border-border rounded-md px-3 py-2 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-foreground/20 focus:border-foreground"
        {...rest}
      />
      {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted pointer-events-none">{suffix}</span>}
    </div>
  );
}
function SaveCancel({ onSave, onCancel, disabled }) {
  return (
    <div className="flex justify-end gap-2 pt-2">
      <button onClick={onCancel} className="text-xs px-4 py-2 text-muted hover:text-foreground inline-flex items-center gap-1"><X className="w-3 h-3" weight="bold" />Cancelar</button>
      <button onClick={onSave} disabled={disabled} data-testid="save-perito-detail" className="bg-foreground text-white text-xs px-4 py-2 rounded-md font-medium disabled:opacity-50 inline-flex items-center gap-1"><FloppyDisk className="w-3 h-3" weight="bold" />Guardar</button>
    </div>
  );
}
